import { FormInstance } from "antd";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";
import { WardConfigurationInjection } from "../../di/WardConfigurationInjection";
import { WardConfigurationFormRequestEntity } from "../../domain/entities/WardConfigurationFormRequestEntity";
import { WardConfigurationListRequestEntity } from "../../domain/entities/WardConfigurationListRequestEntity";
import { IWardConfigurationObservedState } from "../interfaces/IWardConfiguration";

// ─── Input ────────────────────────────────────────────────────────────────────

class WardConfigurationInput implements Disposable {
  public fetchList = new BehaviorSubject<WardConfigurationListRequestEntity>({
    page: 0,
    limit: 10,
  });
  public fetchDetail = new BehaviorSubject<string>("");
  public createWard = new BehaviorSubject<WardConfigurationFormRequestEntity>(
    {},
  );
  public updateWard = new BehaviorSubject<WardConfigurationFormRequestEntity>(
    {},
  );
  public deleteWard = new BehaviorSubject<string>("");

  public fetchProvinces = new BehaviorSubject<void>(undefined);
  public fetchDistricts = new BehaviorSubject<string>(""); // provinceCode

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.createWard.complete();
    this.updateWard.complete();
    this.deleteWard.complete();
    this.fetchProvinces.complete();
    this.fetchDistricts.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class WardConfigurationOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");
  public actionSuccess = new BehaviorSubject<
    "create" | "update" | "delete" | null
  >(null);
  public onGetDetailSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.actionSuccess.complete();
    this.onGetDetailSuccess.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class WardConfigurationViewModel extends BasedObservedViewModel<
  WardConfigurationInput,
  WardConfigurationOutput,
  IWardConfigurationObservedState
> {
  constructor() {
    super(new WardConfigurationInput(), new WardConfigurationOutput());
  }

  private useCase = WardConfigurationInjection.getUseCase();

  protected override createObservedState(): IWardConfigurationObservedState {
    return {
      loading: false,
      formLoading: false,
      detailLoading: false,
      list: [],
      detail: {},
      paging: { page: 0, limit: 10, total: 0 },
      provinceOptions: [],
      districtOptions: [],
      districtLoading: false,
      selectedProvinceCode: "",
    };
  }

  public buildTableQuery(
    pagination: any,
    sorter: any,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: any,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    let order = prevParams?.order;
    if (sorter && sorter.field && sorter.order) {
      const direction = sorter.order === "ascend" ? "ASC" : "DESC";
      order = `${sorter.field} ${direction}`;
    }

    const query = {
      ...prevParams,
      ...formValues,
      page: (pagination?.current ?? 1) - 1,
      limit: pagination?.pageSize ?? 10,
      order,
    };

    return {
      query,
    };
  }

  protected override transform(): void {
    // Fetch list
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.setObservedState({ loading: true });
        const res = await this.useCase.getWardConfigurationList(params);
        if (res) {
          this.setObservedState({
            list: res.data || [],
            paging: res.pagination || {
              page: params.page || 0,
              limit: params.limit || 10,
              total: 0,
            },
          });
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Fetch detail
    this.skipFirstInitial(this.input.fetchDetail, async (id) => {
      if (!id) return;
      try {
        this.setObservedState({ detailLoading: true });
        const res = await this.useCase.getWardConfigurationDetail(id);
        if (res?.data) {
          this.setObservedState({ detail: res.data });
          this.output.onGetDetailSuccess.next(true);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ detailLoading: false });
      }
    });

    // Create ward
    this.skipFirstInitial(this.input.createWard, async (body) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.setObservedState({ formLoading: true });
        const res = await this.useCase.createWardConfiguration(body);
        if (res?.data) {
          this.output.actionSuccess.next("create");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });

    // Update ward
    this.skipFirstInitial(this.input.updateWard, async (body) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.setObservedState({ formLoading: true });
        const res = await this.useCase.updateWardConfiguration(body);
        if (res?.data) {
          this.output.actionSuccess.next("update");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });

    // Delete ward
    this.skipFirstInitial(this.input.deleteWard, async (id) => {
      if (!id) return;
      try {
        this.setObservedState({ loading: true });
        const res = await this.useCase.deleteWardConfiguration(id);
        if (res) {
          this.output.actionSuccess.next("delete");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Fetch Provinces
    this.skipFirstInitial(this.input.fetchProvinces, async () => {
      try {
        const res = await this.useCase.getProvinceList();
        if (res?.data) {
          const options = res.data.map((p) => ({
            label: p.fullName || p.name || "",
            value: p.code || "",
          }));
          this.setObservedState({ provinceOptions: options });
        }
      } catch (error) {
        console.error(error);
      }
    });

    // Fetch Districts
    this.skipFirstInitial(this.input.fetchDistricts, async (provinceCode) => {
      if (!provinceCode) {
        this.setObservedState({
          districtOptions: [],
          selectedProvinceCode: "",
        });
        return;
      }
      try {
        this.setObservedState({
          districtLoading: true,
          selectedProvinceCode: provinceCode,
        });
        const res = await this.useCase.getDistrictList(provinceCode);
        if (res?.data) {
          const options = res.data.map((d) => ({
            label: d.fullName || d.name || "",
            value: d.code || "",
          }));
          this.setObservedState({ districtOptions: options });
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.setObservedState({ districtLoading: false });
      }
    });
  }
}
